import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router'
import { api, type Corbeille, type EntreeDeCorbeille } from '../../lib/api'
import { invaliderFiches } from '../../lib/cache'
import { layout } from '../../design/tokens'

const NATURES: Record<EntreeDeCorbeille['kind'], string> = {
  fiche: 'Fiche',
  agenda: 'Rendez-vous',
  depense: 'Dépense',
  cadeau: 'Cadeau',
}

/**
 * La corbeille.
 *
 * Rien n'a jamais été réellement supprimé : les quatre natures portent une date de suppression
 * depuis l'origine, et toutes les requêtes l'écartent. Ce qui manquait n'était pas la
 * conservation, c'était l'écran pour y revenir — « supprimer » était donc annoncé comme
 * définitif alors qu'il ne l'était pas.
 *
 * Les quatre natures sont réunies plutôt que dispersées dans leurs écrans : on cherche ce qu'on
 * a perdu sans toujours se rappeler ce que c'était.
 */
export function TrashScreen() {
  const { spaceId = '' } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data, isPending } = useQuery<Corbeille>({
    queryKey: ['corbeille', spaceId],
    queryFn: () => api.trash(spaceId),
  })

  const restaurer = useMutation({
    mutationFn: (entree: EntreeDeCorbeille) =>
      api.restoreFromTrash(spaceId, entree.kind, entree.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['corbeille', spaceId] })
      // Tout ce qui lit des fiches, des dates ou de l'argent peut avoir changé.
      await invaliderFiches(queryClient, spaceId)
      await queryClient.invalidateQueries({ queryKey: ['agenda', spaceId] })
      await queryClient.invalidateQueries({ queryKey: ['depenses', spaceId] })
      await queryClient.invalidateQueries({ queryKey: ['cadeaux', spaceId] })
    },
  })

  return (
    <main
      className="mx-auto min-h-dvh bg-surface pb-12"
      style={{ maxWidth: layout.column, paddingInline: layout.gutter }}
    >
      <header className="flex items-center gap-3" style={{ height: layout.header }}>
        <button
          type="button"
          onClick={() => void navigate(-1)}
          aria-label="Retour"
          className="rounded-button border border-border bg-card"
          style={{ width: 44, height: 44 }}
        >
          ←
        </button>
        <h1 className="font-serif text-screen">Corbeille</h1>
      </header>

      {isPending ? (
        <div className="mt-4 rounded-card bg-sunken" style={{ height: 72 }} />
      ) : data && data.entries.length > 0 ? (
        <>
          <p className="text-meta text-ink-muted">
            Ce qui a été supprimé est gardé {data.purgeAfterDays} jours, puis effacé pour de bon.
          </p>

          <ul className="mt-4 flex flex-col gap-2">
            {data.entries.map((entree) => (
              <li
                key={`${entree.kind}-${entree.id}`}
                className="flex items-center gap-3 rounded-card border border-border bg-card p-3"
              >
                <div className="grow">
                  <p className="text-card-title font-semibold">{entree.title}</p>
                  <p className="mt-1 text-meta text-ink-muted">
                    {NATURES[entree.kind]}
                    {entree.detail ? ` · ${entree.detail}` : ''} · {quand(entree.deletedAt)}
                  </p>
                </div>

                <button
                  type="button"
                  disabled={restaurer.isPending}
                  onClick={() => restaurer.mutate(entree)}
                  className="shrink-0 rounded-button bg-accent px-4 text-meta font-semibold text-card"
                  style={{ height: 44 }}
                >
                  Restaurer
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="text-body text-ink-muted">
          La corbeille est vide. Ce que vous supprimez y reste {data?.purgeAfterDays ?? 30} jours.
        </p>
      )}
    </main>
  )
}

/** « il y a 3 jours » plutôt qu'une date : ce qui compte est le temps qu'il reste. */
function quand(iso: string): string {
  const jours = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)

  if (jours <= 0) {
    return "aujourd'hui"
  }

  return jours === 1 ? 'hier' : `il y a ${jours} jours`
}
